import { useMemo } from "react";
import { Layout, SchemaBreadcrumb, SEOHead } from "@/components";
import {
	DictionaryHero,
	DictionaryLoadingSkeleton,
	DictionarySearch,
	EmptyState,
	WordsList,
} from "@/components/dictionary";
import {
	type CategoryOption,
	HorizontalCategoryFilter,
} from "@/components/shared/HorizontalCategoryFilter";
import { useDictionaryFilters } from "@/hooks/use-dictionary-filters";
import { useWords } from "@/hooks/use-words";

const Dicionario = () => {
	const { words, isLoading } = useWords();
	const {
		searchTerm,
		setSearchTerm,
		selectedCategory,
		setSelectedCategory,
		categories,
		filteredWords,
		groupedWords,
	} = useDictionaryFilters(words);

	// Transform categories for the reusable component
	const categoryOptions: CategoryOption[] = useMemo(() => {
		const allOption: CategoryOption = {
			id: "all",
			label: "Todas",
			count: words.length,
		};

		const categoryList = categories.map((cat) => ({
			id: cat,
			label: cat,
			count: words.filter((w) => w.category === cat).length,
		}));

		return [allOption, ...categoryList];
	}, [categories, words]);

	const handleCategoryChange = (categoryId: string) => {
		setSelectedCategory(categoryId === "all" ? null : categoryId);
	};

	return (
		<Layout>
			<SEOHead
				title="Dicionário Imobiliário | Termos e Definições do Mercado"
				description="Glossário completo de termos do mercado imobiliário: construção, direito, documentação, tributário e cadastro. Definições claras para profissionais e cidadãos."
				canonicalUrl="/dicionario"
				keywords="dicionário imobiliário, glossário imobiliário, termos de imóveis, direito imobiliário, documentação imobiliária, cadastro de imóveis"
			/>
			<SchemaBreadcrumb />

			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
				<DictionaryHero />

				<div className="mb-8 space-y-4 animate-slide-up stagger-2">
					<DictionarySearch
						searchTerm={searchTerm}
						onSearchChange={setSearchTerm}
					/>

					{isLoading ? (
						<div className="flex flex-wrap justify-center gap-2">
							{[1, 2, 3, 4, 5].map((i) => (
								<div
									key={i}
									className="h-9 w-24 bg-muted rounded-full animate-pulse"
								/>
							))}
						</div>
					) : (
						<HorizontalCategoryFilter
							categories={categoryOptions}
							activeCategory={selectedCategory ?? "all"}
							onCategoryChange={handleCategoryChange}
						/>
					)}
				</div>

				<div className="mb-6 text-center text-muted-foreground">
					{isLoading ? (
						<div className="h-5 w-40 bg-muted rounded animate-pulse mx-auto" />
					) : (
						<>
							{filteredWords.length} termo
							{filteredWords.length !== 1 ? "s" : ""} encontrado
							{filteredWords.length !== 1 ? "s" : ""}
						</>
					)}
				</div>

				{isLoading ? (
					<DictionaryLoadingSkeleton />
				) : filteredWords.length === 0 ? (
					<EmptyState />
				) : (
					<WordsList groupedWords={groupedWords} />
				)}
			</div>
		</Layout>
	);
};

export default Dicionario;
